import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { ReactSVG } from 'react-svg';
import _debounce from 'lodash/debounce';
import _throttle from 'lodash/throttle';
import ProptTypes from 'prop-types';

import { ismobileDevice, openInNewTab } from '../../../utils';
import { logEventGTM } from '../../../utils/analyticsGTM';

import classes from './index.module.css';

const loadingMapClassList = {
  background: classes['loading-county-map-background'],
  animation: classes['loading-county-map-animation'],
};

const propTypes = {
  onMapLoadError: ProptTypes.func,
  onMapLoadSuccess: ProptTypes.func,
  targetPageUrl: ProptTypes.string,
  svgFileUrl: ProptTypes.string,
  originalUrl: ProptTypes.string,
  gtmEventNames: ProptTypes.objectOf(ProptTypes.any),
  mapTooltip: ProptTypes.objectOf(ProptTypes.any),
  isEmbedWidgetOpen: ProptTypes.bool.isRequired,
};

const defaultProps = {
  onMapLoadError: () => {},
  onMapLoadSuccess: () => {},
  targetPageUrl: '',
  svgFileUrl: '',
  originalUrl: '',
  gtmEventNames: {
    open: '',
  },
  mapTooltip: {
    title: '',
    valueFormatFunc: () => {},
  },
};

const HOVER_BLOCK_DELAY = {
  MOUSE_MOVE_BEGIN: 20,
  MOUSE_MOVE_END: 20,
  CONTENT_UPDATE_FREQUENCY: 200,
};
const HOVER_BLOCK_GAP = 36;

class CountyMapTemplate extends Component {
  constructor(props) {
    super(props);

    this.mapInteractedFlag = false;

    this.mapImgTagRef = createRef();
    this.mapOuterContainerRef = createRef();
    this.hoverBlockRef = createRef();
    this.hoverBlockOverlayRef = createRef();
    this.blockLocationRef = createRef();
    this.blockValueRef = createRef();
  }

  componentDidMount() {
    const { checkImgTagLoad } = this;
    // check if image already cached
    checkImgTagLoad();
  }

  checkImgTagLoad = () => {
    const { mapImgTagRef, onImgTagLoad } = this;
    const mapImgTag = mapImgTagRef && mapImgTagRef.current;

    if (mapImgTag && mapImgTag.complete) {
      onImgTagLoad();
    }
  };

  onImgTagLoad = () => {
    const { mapOuterContainerRef = {} } = this;

    const mapOuterContainer = mapOuterContainerRef && mapOuterContainerRef.current;
    if (mapOuterContainer) {
      mapOuterContainer.classList.remove(loadingMapClassList.background);
    }
  };

  onSvgInjected = (svg) => {
    const { mapOuterContainerRef = {}, prepareMap } = this;
    const { onMapLoadSuccess } = this.props;

    prepareMap(svg);
    onMapLoadSuccess(svg);

    const mapOuterContainer = mapOuterContainerRef && mapOuterContainerRef.current;
    if (mapOuterContainer) {
      mapOuterContainer.classList.remove(loadingMapClassList.animation);
      mapOuterContainer.classList.remove(loadingMapClassList.background);
    }
  };

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { isEmbedWidgetOpen } = this.props;
    return ismobileDevice() && nextProps.isEmbedWidgetOpen !== isEmbedWidgetOpen;
  }

  onMapInteraction = () => {
    const { gtmEventNames } = this.props;
    if (!this.mapInteractedFlag) {
      if (gtmEventNames.open) {
        logEventGTM({
          event: gtmEventNames.open,
        });
      }
      this.mapInteractedFlag = true;
    }
  };

  prepareMap = (svg) => {
    const {
      setHoverBlockVisibility,
      updateHoverBlockPosition,
      updateHoverBlockContent,
      handleCountyClick,
      onMapInteraction,
      hoverBlockRef = {},
    } = this;

    const hoverBlock = hoverBlockRef && hoverBlockRef.current;

    let prevID = null;
    let prevCounty = { style: {} };

    const updateHoverBlockPositionDebounce = _debounce(
      updateHoverBlockPosition,
      HOVER_BLOCK_DELAY.MOUSE_MOVE_END
    );

    const handleHoverBlockPosition = (e) => {
      const { target } = e;
      const { id } = target.children[0];

      if (id !== prevID) {
        prevCounty.style.strokeWidth = '0.1';
        prevCounty.style.pointerEvents = 'auto';
        updateHoverBlockPositionDebounce(svg, target);
        prevID = id;
        prevCounty = target;
        onMapInteraction();
      }
    };

    const positionUpdateDelay = ismobileDevice() ? 0 : HOVER_BLOCK_DELAY.MOUSE_MOVE_BEGIN;
    const contentUpdateDelay = ismobileDevice() ? 0 : HOVER_BLOCK_DELAY.CONTENT_UPDATE_FREQUENCY;

    const handleMouseMoveThrottle = _throttle(handleHoverBlockPosition, positionUpdateDelay);
    const updateHoverBlockContentThrottle = _throttle(updateHoverBlockContent, contentUpdateDelay);

    if (ismobileDevice()) {
      svg.children[2] &&
        svg.children[2].addEventListener('click', (e) => {
          handleMouseMoveThrottle(e);
          updateHoverBlockContentThrottle(e);
        });
    } else {
      svg.children[2] &&
        svg.children[2].addEventListener('mousemove', (e) => {
          handleMouseMoveThrottle(e);
          updateHoverBlockContentThrottle(e);
        });
    }

    if (hoverBlock) {
      svg.onmouseleave = () => {
        setHoverBlockVisibility(false);
        prevCounty.style.strokeWidth = 0.1;
      };
    }

    svg.onclick = (e) => {
      handleCountyClick(e);
    };
  };

  handleCountyClick = (e) => {
    const { targetPageUrl } = this.props;
    if (targetPageUrl) {
      openInNewTab(targetPageUrl);
    }
  };

  updateHoverBlockPosition = (svg, hoverTarget) => {
    const { hoverBlockRef = {}, setHoverBlockVisibility } = this;

    const hoverBlock = hoverBlockRef.current;

    if (!hoverBlock) {
      return;
    }

    let hoverBlockClassName = classes['hover-block'];
    const hoverBlockClassNameOverflownX = classes['hover-block-overflown-x'];
    const hoverBlockClassNameOverflownY = classes['hover-block-overflown-y'];

    const county = hoverTarget.getBoundingClientRect();
    const hoverBlockRect = hoverBlock.getBoundingClientRect();
    const containerRect = svg.getBoundingClientRect();

    const containerLeft = containerRect.left;
    const containerTop = containerRect.top;
    const containerWidth = containerRect.width;

    const hoverBlockWidth = hoverBlockRect.width;
    const hoverBlockHeight = hoverBlockRect.height;

    let right = 'auto';
    let top;
    let left;

    const leftTemp = county.x - containerLeft + county.width / 2;
    const topTemp = county.y - containerTop;

    // Always keep indicator inside map
    if (leftTemp + hoverBlockWidth - HOVER_BLOCK_GAP > containerWidth) {
      // overflown x axis
      right = `${containerWidth - leftTemp}px`;
      left = 'auto';
      hoverBlockClassName += ` ${hoverBlockClassNameOverflownX}`;
    } else {
      left = `${leftTemp}px`;
    }

    if (HOVER_BLOCK_GAP + hoverBlockHeight > topTemp) {
      // overlown y axis
      hoverBlockClassName += ` ${hoverBlockClassNameOverflownY}`;
      top = `${topTemp + hoverBlockHeight + HOVER_BLOCK_GAP + county.height}px`;
    } else {
      top = `${topTemp}px`;
    }

    setHoverBlockVisibility(true);
    hoverBlock.className = hoverBlockClassName;
    hoverBlock.style.left = left;
    hoverBlock.style.right = right;
    hoverBlock.style.top = top;

    hoverTarget.style.strokeWidth = '1';
    hoverTarget.style.pointerEvents = 'none';
  };

  updateHoverBlockContent = (e) => {
    const { mapTooltip } = this.props;
    const { blockLocationRef = {}, blockValueRef = {}, formatCountyName } = this;

    const { valueFormatFunc = () => {} } = mapTooltip || {};

    const { target } = e;

    const countyLocation = blockLocationRef.current;
    const countyValue = blockValueRef.current;

    if (countyLocation) {
      countyLocation.innerHTML = formatCountyName(target.children[0].innerHTML);
    }
    if (countyValue) {
      countyValue.innerHTML = valueFormatFunc(target.dataset.value);
    }
  };

  setHoverBlockVisibility = (isVisible) => {
    const { hoverBlockRef = {}, hoverBlockOverlayRef = {} } = this;
    const hoverBlock = hoverBlockRef.current;
    const hoverBlockOverlay = hoverBlockOverlayRef.current;
    if (hoverBlock.style) {
      hoverBlock.style.visibility = isVisible ? 'visible' : 'hidden';
    }
    if (hoverBlockOverlay.style) {
      hoverBlockOverlay.style.visibility = isVisible ? 'visible' : 'hidden';
    }
  };

  formatCountyName = (countyName) => countyName.replace(',', ' County,');

  getHoverBlockView = () => {
    const { mapTooltip } = this.props;
    const {
      setHoverBlockVisibility,
      hoverBlockRef,
      hoverBlockOverlayRef,
      blockLocationRef,
      blockValueRef,
    } = this;
    const { title = '' } = mapTooltip || {};
    return (
      <>
        <div
          ref={hoverBlockOverlayRef}
          className={classes['hover-block-mobile-overlay']}
          onClick={() => setHoverBlockVisibility(false)}
        />
        <div
          ref={hoverBlockRef}
          className={classes['hover-block']}
          onClick={() => setHoverBlockVisibility(false)}
        >
          <div className={classes['hover-block-location-row']}>
            <i className="icon-map" />
            <span className={classes['hover-block-location-name']} ref={blockLocationRef} />
          </div>
          <div className={classes['hover-block-col']}>
            {title && <span className={classes['hover-block-title']}>{title}</span>}
            <span className={classes['hover-block-value']} ref={blockValueRef} />
          </div>
        </div>
      </>
    );
  };

  getMapView = () => {
    const { onSvgInjected, mapOuterContainerRef, mapImgTagRef, onImgTagLoad } = this;
    const { onMapLoadError, svgFileUrl, originalUrl } = this.props;

    return (
      <div
        className={`${classes['county-map-outer']} ${loadingMapClassList.background} ${loadingMapClassList.animation}`}
        ref={mapOuterContainerRef}
      >
        <img
          ref={mapImgTagRef}
          className={classes['county-map-img']}
          onLoad={onImgTagLoad}
          src={originalUrl}
        />
        <ReactSVG
          src={svgFileUrl}
          className={classes['county-map-svg']}
          afterInjection={(error, svg) => {
            if (error) {
              onMapLoadError(error);
              return;
            }
            onSvgInjected(svg);
          }}
        />
      </div>
    );
  };

  render() {
    const { getHoverBlockView, getMapView } = this;

    const hoverBlockView = getHoverBlockView();
    const mapView = getMapView();

    return (
      <>
        {hoverBlockView}
        {mapView}
      </>
    );
  }
}

const mapStateToProps = ({ modals: { embedWidget } }) => {
  const { open } = embedWidget;
  return {
    isEmbedWidgetOpen: open,
  };
};

CountyMapTemplate.propTypes = propTypes;
CountyMapTemplate.defaultProps = defaultProps;

export default connect(mapStateToProps)(CountyMapTemplate);
